// src/lib/subscriptionStatus.ts
// NOTE: No firebase/firestore import — uses structural typing so this module
// has zero external deps and is safely loadable in any test environment.

export type SubscriptionStatus =
  | 'trialing'
  | 'trial_expired'
  | 'active'
  | 'canceled'
  | 'past_due'

/** A minimal shape compatible with firebase/firestore Timestamp */
interface TimestampLike {
  toDate(): Date
  toMillis(): number
}

export interface UserSubscription {
  subscriptionStatus: SubscriptionStatus
  trialStartedAt?: TimestampLike
  trialEndsAt?: TimestampLike
  /** Legacy numeric field: trial end time in milliseconds since epoch */
  trialEndsAtMs?: number
}

export function resolveAccess(sub: UserSubscription): {
  hasAccess: boolean
  isTrialing: boolean
  trialExpired: boolean
  daysRemaining: number | null
} {
  const now = new Date()

  if (sub.subscriptionStatus === 'active') {
    return { hasAccess: true, isTrialing: false, trialExpired: false, daysRemaining: null }
  }

  if (sub.subscriptionStatus === 'trialing') {
    // Resolve end-date from either trialEndsAt (Timestamp) or trialEndsAtMs (number)
    let endMs: number | null = null
    if (sub.trialEndsAt) {
      endMs = sub.trialEndsAt.toMillis()
    } else if (typeof sub.trialEndsAtMs === 'number' && sub.trialEndsAtMs > 0) {
      endMs = sub.trialEndsAtMs
    }

    // No end date on record — fail open rather than locking the user out.
    // The billing system is the authoritative source; a missing end date here
    // means the mirror write hasn't caught up yet, not that the trial is over.
    if (endMs === null) {
      return { hasAccess: true, isTrialing: true, trialExpired: false, daysRemaining: null }
    }

    const hasAccess = now.getTime() < endMs
    const daysRemaining = hasAccess
      ? Math.ceil((endMs - now.getTime()) / (1000 * 60 * 60 * 24))
      : 0
    return {
      hasAccess,
      isTrialing: hasAccess,
      trialExpired: !hasAccess,
      daysRemaining,
    }
  }

  return { hasAccess: false, isTrialing: false, trialExpired: true, daysRemaining: null }
}
